import React, { memo, useEffect, useState, useRef } from 'react';
import LoadingPlaceholder from './LoadingPlaceholder';

const LazyLoadOnView = memo<{
  children: React.ReactNode;
  height?: string;
  rootMargin?: string;
}>(({ 
  children, 
  height = '12rem',
  rootMargin = '200px' 
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  
  // Unified effect for visibility detection
  useEffect(() => {
    // Skip if no ref or we're not in a browser environment
    if (!ref.current || typeof window === 'undefined') {
      return;
    }

    // Initial visibility check - show immediately if in viewport
    const boundingRect = ref.current.getBoundingClientRect();
    const rootMarginPx = parseInt(rootMargin);
    const isInitiallyVisible = 
      boundingRect.top < window.innerHeight + rootMarginPx &&
      boundingRect.bottom > -rootMarginPx;
    
    if (isInitiallyVisible) {
      setIsVisible(true);
      return; // No need to observe if already visible
    }

    // Set up IntersectionObserver for elements not initially visible
    if (typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect(); // Stop observing once visible
          }
        },
        { rootMargin, threshold: 0.1 }
      );

      observer.observe(ref.current);
      return () => observer.disconnect();
    } else {
      // Fallback for browsers without IntersectionObserver
      setIsVisible(true);
    }
  }, [rootMargin]);

  return (
    <div 
      ref={ref} 
      style={{
        containIntrinsicSize: `0 ${height}`,
        contentVisibility: 'auto'
      }}
    >
      {isVisible ? children : <LoadingPlaceholder height={height} />}
    </div>
  );
});

LazyLoadOnView.displayName = 'LazyLoadOnView';

export default LazyLoadOnView;